import { storableError } from '../util/errors';
import { googleStorageAPI } from '../util/api';
import { MEDIA_KIND, normalizeUploadResponse } from '../util/uploadedMedia';
import { prepareImageForUpload, IMAGE_UPLOAD_MAX } from '../util/resizeImage';
import { avatarImageEntity } from '../util/memberAvatar';

// ================ Action types ================ //

export const UPLOAD_DOCUMENT_REQUEST = 'app/googleStorage/UPLOAD_DOCUMENT_REQUEST';
export const UPLOAD_DOCUMENT_SUCCESS = 'app/googleStorage/UPLOAD_DOCUMENT_SUCCESS';
export const UPLOAD_DOCUMENT_ERROR = 'app/googleStorage/UPLOAD_DOCUMENT_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/googleStorage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/googleStorage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/googleStorage/UPLOAD_IMAGE_ERROR';

export const UPLOAD_VIDEO_REQUEST = 'app/googleStorage/UPLOAD_VIDEO_REQUEST';
export const UPLOAD_VIDEO_SUCCESS = 'app/googleStorage/UPLOAD_VIDEO_SUCCESS';
export const UPLOAD_VIDEO_ERROR = 'app/googleStorage/UPLOAD_VIDEO_ERROR';

// ================ Reducer ================ //

const initialState = {
  uploadDocumentInProgress: false,
  uploadDocumentError: null,
  uploadImageInProgress: false,
  uploadImageError: null,
  uploadVideoInProgress: false,
  uploadVideoError: null,
};

export default function googleStorageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_DOCUMENT_REQUEST:
      return { ...state, uploadDocumentInProgress: true, uploadDocumentError: null };
    case UPLOAD_DOCUMENT_SUCCESS:
      return { ...state, uploadDocumentInProgress: false, uploadDocumentError: null };
    case UPLOAD_DOCUMENT_ERROR:
      return { ...state, uploadDocumentInProgress: false, uploadDocumentError: payload };

    case UPLOAD_IMAGE_REQUEST:
      return { ...state, uploadImageInProgress: true, uploadImageError: null };
    case UPLOAD_IMAGE_SUCCESS:
      return { ...state, uploadImageInProgress: false, uploadImageError: null };
    case UPLOAD_IMAGE_ERROR:
      return { ...state, uploadImageInProgress: false, uploadImageError: payload };

    case UPLOAD_VIDEO_REQUEST:
      return { ...state, uploadVideoInProgress: true, uploadVideoError: null };
    case UPLOAD_VIDEO_SUCCESS:
      return { ...state, uploadVideoInProgress: false, uploadVideoError: null };
    case UPLOAD_VIDEO_ERROR:
      return { ...state, uploadVideoInProgress: false, uploadVideoError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const uploadDocumentRequest = () => ({ type: UPLOAD_DOCUMENT_REQUEST });
export const uploadDocumentSuccess = () => ({ type: UPLOAD_DOCUMENT_SUCCESS });
export const uploadDocumentError = error => ({
  type: UPLOAD_DOCUMENT_ERROR,
  error: true,
  payload: error,
});

export const uploadImageRequest = () => ({ type: UPLOAD_IMAGE_REQUEST });
export const uploadImageSuccess = () => ({ type: UPLOAD_IMAGE_SUCCESS });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  error: true,
  payload: error,
});

export const uploadVideoRequest = () => ({ type: UPLOAD_VIDEO_REQUEST });
export const uploadVideoSuccess = () => ({ type: UPLOAD_VIDEO_SUCCESS });
export const uploadVideoError = error => ({
  type: UPLOAD_VIDEO_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

// Per-kind config so we don't repeat ourselves below
const KIND_CONFIG = {
  [MEDIA_KIND.IMAGE]: {
    formField: 'image',
    api: () => googleStorageAPI.images.upload,
    requestAction: uploadImageRequest,
    successAction: uploadImageSuccess,
    errorAction: uploadImageError,
  },
  [MEDIA_KIND.VIDEO]: {
    formField: 'video',
    api: () => googleStorageAPI.videos.upload,
    requestAction: uploadVideoRequest,
    successAction: uploadVideoSuccess,
    errorAction: uploadVideoError,
  },
  [MEDIA_KIND.DOCUMENT]: {
    formField: 'document',
    api: () => googleStorageAPI.documents.upload,
    requestAction: uploadDocumentRequest,
    successAction: uploadDocumentSuccess,
    errorAction: uploadDocumentError,
  },
};

/**
 * Unified upload thunk. Use this for any new code:
 *   await dispatch(uploadMedia(file, MEDIA_KIND.IMAGE));
 *
 * Returns a normalized media object (or null on failure).
 */
export const uploadMedia = (file, kind = MEDIA_KIND.IMAGE) => async dispatch => {
  const config = KIND_CONFIG[kind];
  if (!config) {
    throw new Error(`uploadMedia: unknown kind "${kind}"`);
  }

  dispatch(config.requestAction());

  const formData = new FormData();
  formData.append(config.formField, file);

  try {
    const response = await config.api()(formData);
    dispatch(config.successAction());
    return normalizeUploadResponse(response, kind, file);
  } catch (e) {
    dispatch(config.errorAction(storableError(e)));
    return null;
  }
};

/**
 * A member's avatar: resized in the browser, stored in our own storage, and
 * returned in the shape of a Sharetribe image (see util/memberAvatar.js), so
 * the profile forms preview it as before. Rejects if the upload fails.
 */
export const uploadMemberAvatar = file => async dispatch => {
  const resized = await prepareImageForUpload(file, { maxDimension: IMAGE_UPLOAD_MAX.avatar });
  const media = await dispatch(uploadMedia(resized, MEDIA_KIND.IMAGE));
  if (!media || !media.link) {
    throw new Error('Avatar upload failed');
  }
  return avatarImageEntity(media);
};

// Legacy thunks — kept for back-compat with existing call sites. New
// code should use uploadMedia(file, MEDIA_KIND.X) instead.
export const uploadImage = file => uploadMedia(file, MEDIA_KIND.IMAGE);
export const uploadVideo = file => uploadMedia(file, MEDIA_KIND.VIDEO);
export const uploadDocument = file => uploadMedia(file, MEDIA_KIND.DOCUMENT);

// ================ Selectors ================ //

const selectGoogleStorage = state => state.googleStorage || initialState;

export const selectUploadInProgress = (state, kind = MEDIA_KIND.IMAGE) => {
  const s = selectGoogleStorage(state);
  if (kind === MEDIA_KIND.VIDEO) return s.uploadVideoInProgress;
  if (kind === MEDIA_KIND.DOCUMENT) return s.uploadDocumentInProgress;
  return s.uploadImageInProgress;
};

export const selectUploadError = (state, kind = MEDIA_KIND.IMAGE) => {
  const s = selectGoogleStorage(state);
  if (kind === MEDIA_KIND.VIDEO) return s.uploadVideoError;
  if (kind === MEDIA_KIND.DOCUMENT) return s.uploadDocumentError;
  return s.uploadImageError;
};
